

import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { CornerDownRight, Settings } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Multiselect } from "@/components/ui/multiselect";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCommunication } from "@/lib/CommunicationWrapper";

export const NotificationsTab = () => {
	const { updateOptions, useRerenderListener } = useCommunication();
	const { options } = useRerenderListener([
		"todo_reminders_time",
		"notification_preferences",
		"extension_updates",
		"show_changelog_popups",
		"browser_show_notifications"
	]);

	const notificationPrefs = options.notification_preferences || {
		task_due_soon: { browser: true },
		task_due_today: { browser: true },
		overdue_task: { browser: false },
	};

	// Hours before due time to remind for "Task Due Soon" notifications
	const todoReminderHours: number = options.todo_reminders_time ?? 1;

	// Preset hour options
	const presetReminderOptions: number[] = [1, 2, 3, 6, 12, 24];

	const extensionUpdates = options.extension_updates ?? false;
	const showChangelogPopups = options.show_changelog_popups ?? true;

	const updateNotificationPref = (taskType: "task_due_soon" | "task_due_today" | "overdue_task", method: "browser", value: boolean) => {
		const newPrefs = {
			...notificationPrefs,
			[taskType]: {
				...notificationPrefs[taskType],
				[method]: value,
			},
		};
		updateOptions({ notification_preferences: newPrefs });
	};

	const updateTodoReminderTime = (hours: number) => {
		updateOptions({ todo_reminders_time: hours });
	};

	return (
		<div className="flex flex-col gap-4 p-4 h-full">
			<h1 className="font-semibold text-lg">Notifications</h1>

			<div className="bg-theme-0 rounded-lg border border-theme p-4">
				<div className="grid gap-4 mb-4 grid-cols-[50%_1fr_1fr] text-sm">
					<div className="font-medium">Type</div>
					<div className="text-right">
						<Tooltip>
							<TooltipTrigger>
								<div className="font-medium text-theme-2 opacity-60 cursor-help">Email</div>
							</TooltipTrigger>
							<TooltipContent>Coming soon</TooltipContent>
						</Tooltip>
					</div>
					<div className="text-right">
						<div className="font-medium justify-end flex items-center gap-1">
							<span>Browser</span>
							<Popover>
								<PopoverTrigger className="cursor-pointer hover:scale-105 transition-all duration-300">
									<Settings className="size-4 text-theme-1" />
								</PopoverTrigger>
								<PopoverContent className="p-3 bg-theme-0 border-theme">
									<div className="flex flex-col justify-between gap-1">
										<p className="text-sm font-medium">Display on</p>
										<Multiselect
											options={[
												{ value: "non_canvas", label: "Non-Canvas pages" },
												{ value: "canvas", label: "Canvas pages" },

											]}
											value={options.browser_show_notifications || []}
											onValueChange={(newValue) => updateOptions({ browser_show_notifications: newValue })}
											placeholder="Select pages"
											variant="default"
											triggerClassName="h-[32px] cursor-pointer"
										/>
									</div>
								</PopoverContent>
							</Popover>
						</div>
					</div>
				</div>

				<div className="space-y-3">
					<div className="grid gap-4 items-center grid-cols-[50%_1fr_1fr]">
						<div className="text-sm flex items-center gap-2">
							<span>Task Due Soon</span>
							{notificationPrefs.task_due_soon.browser && (
								<Popover>
									<PopoverTrigger className="cursor-pointer hover:scale-105 transition-all duration-300">
										<Settings className="size-4 text-theme-1" />
									</PopoverTrigger>
									<PopoverContent className="p-3">
										<div className="flex flex-col justify-between gap-1">
											<h3 className="text-sm font-medium">Remind me</h3>
											<div className="flex">
												<div className="flex items-center gap-2 text-xs">
													<Select value={String(todoReminderHours)} onValueChange={(value) => updateTodoReminderTime(parseInt(value))}>
														<SelectTrigger className="h-[32px] rounded-md border border-theme bg-theme-0 px-2">
															<SelectValue />
														</SelectTrigger>
														<SelectContent className="z-100">
															{presetReminderOptions.map((hours) => (
																<SelectItem key={hours} value={hours.toString()}>
																	{`${hours} hour${hours === 1 ? "" : "s"}`}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
													<span> before due</span>
												</div>
											</div>
										</div>
									</PopoverContent>
								</Popover>
							)}
						</div>
						<div className="flex justify-end">
							<Tooltip>
								<TooltipTrigger>
									<Switch height={20} checked={false} variant="default" disabled className="opacity-40" />
								</TooltipTrigger>
								<TooltipContent>Coming soon</TooltipContent>
							</Tooltip>
						</div>
						<div className="flex justify-end">
							<Switch height={20} checked={notificationPrefs.task_due_soon.browser} onCheckedChange={(checked) => updateNotificationPref("task_due_soon", "browser", checked === true)} />
						</div>
					</div>

					<div className="grid gap-4 items-center grid-cols-[50%_1fr_1fr]">
						<div className="text-sm">Extension Updates</div>
						<div className="flex justify-end">
							<Tooltip>
								<TooltipTrigger>
									<Switch height={20} checked={false} disabled className="opacity-40" />
								</TooltipTrigger>
								<TooltipContent>Coming soon</TooltipContent>
							</Tooltip>
						</div>
						<div className="flex justify-end">
							<Switch height={20} checked={extensionUpdates} onCheckedChange={(checked) => updateOptions({ extension_updates: checked })} />
						</div>
					</div>

				</div>
			</div>

			<div className="space-y-2">
				<div className="border border-theme bg-theme-0 rounded-lg p-3 flex items-center justify-between gap-2">
					<div className="flex items-center justify-between gap-2 w-full">
						<p className="text-sm">Show changelog popups</p>
						<Switch height={20} checked={showChangelogPopups} onCheckedChange={(checked) => updateOptions({ show_changelog_popups: checked })} />
					</div>
				</div>
			</div>
		</div>
	);
};
